import { ChatMessageGroup } from '@provision/chat-utils';
import React, { useEffect, useMemo, useState } from 'react';
import { usePrevious } from 'react-querybuilder';
import { differenceWith } from 'remeda';
import { useTextWithUIPrepareMetaData } from 'src/components/TextWithUI/use-text-with-ui';
import { useConversation } from 'src/graphql/entities/conversation/conversation';
import type { ConversationParticipant } from 'src/graphql/entities/conversation/conversation.types';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import {
  ChatMessageContext,
  ChatMessageContextValue,
  InitialPhase,
} from './chat-message-context';

export const ChatMessageProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const conversationId = useChatCommonData((s) => s.conversationId);
  const partnerId = useChatCommonData((s) => s.partnerId);

  const partnerIdNumber = useMemo(() => {
    const parsed = Number(partnerId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [partnerId]);

  const {
    messages,
    loading: isConversationLoading,
    loadMore,
    hasMore,
    isLoadingMore,
    updateMessage,
    editMessage,
  } = useConversation({
    conversationId,
    partnerId: partnerIdNumber,
  });

  const { metaData, loadMetadata } = useTextWithUIPrepareMetaData();

  const previousMessages = usePrevious(messages);
  const [messagesWithMetadata, setMessagesWithMetadata] = useState<
    ChatMessageGroup<ConversationParticipant>[]
  >([]);
  const [initialPhase, setInitialPhase] = useState<InitialPhase>(InitialPhase.Idle);
  const [hasProcessedInitialMessageBatch, setHasProcessedInitialMessageBatch] =
    useState(false);

  useEffect(() => {
    setInitialPhase(InitialPhase.Idle);
    setMessagesWithMetadata([]);
    setHasProcessedInitialMessageBatch(false);
  }, [conversationId]);

  useEffect(() => {
    let stale = false;

    const fetchAddedMessagesMetaData = async (): Promise<void> => {
      if (messages.length === 0) {
        if (!isConversationLoading) {
          setInitialPhase(InitialPhase.Ready);
        }
        setMessagesWithMetadata([]);
        return;
      }

      if (!hasProcessedInitialMessageBatch) {
        setInitialPhase(InitialPhase.Hydrating);
      }

      const addedMessages = differenceWith(
        messages,
        previousMessages || [],
        (a, b) => a.id === b.id,
      );

      if (addedMessages.length) {
        await loadMetadata(
          addedMessages.reduce(
            (acc, group) =>
              acc + group.messages.reduce((messageAcc, it) => messageAcc + it.message, ''),
            '',
          ),
        );
      }

      if (stale) {
        return;
      }

      setMessagesWithMetadata(messages);

      if (!hasProcessedInitialMessageBatch) {
        setHasProcessedInitialMessageBatch(true);
        setInitialPhase(InitialPhase.Ready);
      }
    };

    void fetchAddedMessagesMetaData();

    return () => {
      stale = true;
    };
  }, [
    hasProcessedInitialMessageBatch,
    isConversationLoading,
    loadMetadata,
    messages,
    previousMessages,
  ]);

  const value = useMemo<ChatMessageContextValue>(
    () => ({
      onUpdateMessage: updateMessage,
      onEditMessage: editMessage,
      metaData,
      messagesWithMetadata,
      initialPhase,
      isConversationLoading,
      hasMore,
      isLoadingMore,
      loadMore,
    }),
    [
      updateMessage,
      editMessage,
      metaData,
      messagesWithMetadata,
      initialPhase,
      isConversationLoading,
      hasMore,
      isLoadingMore,
      loadMore,
    ],
  );

  return <ChatMessageContext.Provider value={value}>{children}</ChatMessageContext.Provider>;
};
